import { useQuery } from 'react-query';
import { getAtsSummary, PIPELINE_STAGES } from '../../../../network/ats';

const STAGE_LABEL = Object.fromEntries(
  PIPELINE_STAGES.map((stage) => [stage.value, stage.label]),
) as Record<string, string>;

const Stat = ({ label, value }: { label: string; value: number }) => (
  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
    <p className="text-sm text-gray-600">{label}</p>
    <p className="mt-1 text-3xl font-semibold text-gray-900">{value}</p>
  </div>
);

/** Recruitment summary from the API: counts by stage and by vacancy. */
export function Dashboard() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['ats-summary'],
    queryFn: getAtsSummary,
  });

  if (isLoading) {
    return <p className="p-6 text-gray-500">Loading the recruitment summary…</p>;
  }
  if (isError || !data) {
    return (
      <p role="alert" className="p-6 text-red-600">
        The recruitment summary could not be loaded. Please refresh the page.
      </p>
    );
  }

  const maxStage = Math.max(1, ...data.byStage.map((s) => s.count));

  return (
    <div className="flex flex-col gap-4 rounded-xl bg-white p-4 shadow sm:p-6">
      <h2 className="text-lg font-semibold text-gray-900">Recruitment summary</h2>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Candidates" value={data.totals.candidates} />
        <Stat label="Open vacancies" value={data.totals.openVacancies} />
        <Stat label="Closed vacancies" value={data.totals.closedVacancies} />
        <Stat
          label="Interviews in the next 7 days"
          value={data.totals.upcomingInterviews}
        />
      </div>

      <section aria-labelledby="ats-by-stage">
        <h3 id="ats-by-stage" className="mb-2 font-medium text-gray-900">
          Candidates by stage
        </h3>
        <ul className="flex flex-col gap-2">
          {data.byStage.map(({ stage, count }) => (
            <li key={stage} className="flex items-center gap-3 text-sm">
              <span className="w-24 shrink-0 text-gray-700">
                {STAGE_LABEL[stage] ?? stage}
              </span>
              <span className="h-3 flex-1 rounded bg-gray-100">
                <span
                  className="block h-3 rounded bg-red-400"
                  style={{ width: `${(count / maxStage) * 100}%` }}
                />
              </span>
              <span className="w-10 text-right font-medium text-gray-900">
                {count}
              </span>
            </li>
          ))}
        </ul>
      </section>

      <section aria-labelledby="ats-by-vacancy" className="overflow-x-auto">
        <h3 id="ats-by-vacancy" className="mb-2 font-medium text-gray-900">
          Candidates by vacancy
        </h3>
        {data.byVacancy.length === 0 ? (
          <p className="text-sm text-gray-500">No vacancies yet.</p>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="text-left text-xs uppercase text-gray-500">
              <tr>
                <th scope="col" className="py-2 pr-4">
                  Vacancy
                </th>
                {PIPELINE_STAGES.map((stage) => (
                  <th key={stage.value} scope="col" className="px-2 py-2 text-right">
                    {stage.label}
                  </th>
                ))}
                <th scope="col" className="py-2 pl-2 text-right">
                  Total
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.byVacancy.map((vacancy) => (
                <tr key={vacancy.id}>
                  <td className="py-2 pr-4 text-gray-900">
                    {vacancy.title}
                    {vacancy.status === 'closed' && (
                      <span className="ml-2 text-xs text-gray-500">(closed)</span>
                    )}
                  </td>
                  {PIPELINE_STAGES.map((stage) => (
                    <td key={stage.value} className="px-2 py-2 text-right text-gray-700">
                      {vacancy.byStage[stage.value] ?? 0}
                    </td>
                  ))}
                  <td className="py-2 pl-2 text-right font-medium">{vacancy.total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {data.totals.unassignedCandidates > 0 && (
          <p className="mt-2 text-xs text-gray-500">
            {data.totals.unassignedCandidates} candidate
            {data.totals.unassignedCandidates === 1 ? ' is' : 's are'} not linked
            to a vacancy.
          </p>
        )}
      </section>
    </div>
  );
}
